import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { recipesCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { Link } from '@/ui/Link/Link'
import { Inline } from '@/ui/layout/Inline'
import { Text } from '@/ui/layout/Text'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { StatusBadge } from '@/ui/Status/Status'
import { paramText, type RecipeInstall, recipesApi } from '../../api/recipes'
import { ROUTES } from '../../constants/routes'
import { useWorkspaceAgentsQuery } from '../../hooks/queries'
import { Fact, Facts } from '../Facts'
import { invalidateRecipes } from './invalidateRecipes'
import { RecipeWarnings } from './RecipeWarnings'

interface Props {
  workspaceId: string
  install: RecipeInstall
  onEdit: () => void
}

const reasonOf = (e: unknown) => (e instanceof Error ? e.message : String(e))

/** What a set-up recipe does right now, with pause/resume and removal. */
export function RecipeInstallCard({ workspaceId, install, onEdit }: Props) {
  const qc = useQueryClient()
  const { data: agents = [] } = useWorkspaceAgentsQuery(workspaceId)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      recipesApi.setEnabled(workspaceId, install.id, enabled),
    onSuccess: () => invalidateRecipes(qc, workspaceId),
  })
  const remove = useMutation({
    mutationFn: () => recipesApi.remove(workspaceId, install.id),
    onSuccess: () => invalidateRecipes(qc, workspaceId),
    // The dialog shows no error of its own: it closes and the card reports.
    onError: () => setConfirmRemove(false),
  })

  const agent = agents.find((a) => a.agent_id === install.agent_id)
  const warnings = [
    install.readiness?.state === 'needs_setup'
      ? recipesCopy.install.needsSetup(
          install.source.name,
          install.readiness.reason ?? '',
        )
      : null,
    ...install.warnings,
  ].filter((w): w is string => w !== null)
  const error = toggle.isError
    ? toggle.variables
      ? recipesCopy.install.resumeFailed(reasonOf(toggle.error))
      : recipesCopy.install.pauseFailed(reasonOf(toggle.error))
    : remove.isError
      ? recipesCopy.install.removeFailed(reasonOf(remove.error))
      : null

  return (
    <Card
      header={
        <Inline gap="space-8">
          <Text weight="strong">{install.recipe_title}</Text>
          {/* An installed recipe that is on shows no badge (D192). */}
          {!install.enabled && (
            <StatusBadge tone="warning">
              {recipesCopy.status.paused}
            </StatusBadge>
          )}
        </Inline>
      }
      footer={
        <>
          <Button
            variant="outline"
            ground="surface"
            loading={toggle.isPending}
            onClick={() => toggle.mutate(!install.enabled)}
          >
            {install.enabled
              ? recipesCopy.install.pause
              : recipesCopy.install.resume}
          </Button>
          <Button variant="ghost" ground="surface" onClick={onEdit}>
            {recipesCopy.install.change}
          </Button>
          <Button
            variant="ghost"
            ground="surface"
            onClick={() => setConfirmRemove(true)}
          >
            {recipesCopy.install.remove}
          </Button>
        </>
      }
    >
      <Facts>
        <Fact label={recipesCopy.install.source}>
          <Link href={ROUTES.resourceDetail(install.source.id)}>
            {install.source.name}
          </Link>
        </Fact>
        <Fact label={recipesCopy.install.topic}>
          <Link href={ROUTES.topic(install.topic_id)}>
            {recipesCopy.install.topicName(
              install.topic_name ?? recipesCopy.install.topicFallback,
            )}
          </Link>
        </Fact>
        <Fact label={recipesCopy.install.agent}>
          {agent?.name ?? install.agent_id ?? (
            <StatusBadge tone="warning">
              {recipesCopy.status.noAgent}
            </StatusBadge>
          )}
        </Fact>
        {install.param_fields.map((f) => {
          const value = install.params[f.key]
          const shown =
            f.kind === 'select'
              ? (f.options.find((o) => o.value === value)?.label ??
                String(value ?? ''))
              : paramText(f, value)
          return (
            <Fact key={f.key} label={f.label}>
              {shown || <Text tone="text-mute">{recipesCopy.install.any}</Text>}
            </Fact>
          )
        })}
      </Facts>
      <RecipeWarnings warnings={warnings} />
      {error && <Alert tone="danger">{error}</Alert>}
      <ConfirmDialog
        open={confirmRemove}
        title={recipesCopy.install.removeTitle}
        description={recipesCopy.install.removeDescription}
        confirmLabel={recipesCopy.install.removeConfirm}
        tone="danger"
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmRemove(false)}
      />
    </Card>
  )
}
